import React, { useRef, useState } from 'react';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { useLanguage } from '@/state/language/LanguageProvider';

type KpiOption = {
  id: string;
  label: string;
};

type ObjectiveKpiRequiredProps = {
  options: KpiOption[];
  disabled?: boolean;
  onAttach: (kpiId: string) => Promise<void>;
};

/**
 * Card cue when an objective has no catalog KPI. "Add" reveals the same
 * type-to-narrow picker used on create/edit so the list is usable at scale.
 */
export const ObjectiveKpiRequired: React.FC<ObjectiveKpiRequiredProps> = ({
  options,
  disabled = false,
  onAttach
}) => {
  const { t } = useLanguage();
  const [picking, setPicking] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const attachingRef = useRef(false);

  const handlePick = async (kpiId: string) => {
    setSelectedId(kpiId);
    // Autocomplete fires onSelect and onChange for the same pick; attach once.
    if (!kpiId || attachingRef.current) {
      return;
    }
    attachingRef.current = true;
    try {
      await onAttach(kpiId);
    } finally {
      attachingRef.current = false;
    }
  };

  return (
    <div className="mt-1 flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-red-600">
        {t('alignment.objectives.kpiRequired')}
      </span>
      {!picking ? (
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="text-xs font-medium px-2 py-0.5 rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]"
        >
          {t('alignment.objectives.addKpi')}
        </button>
      ) : (
        <div className="w-64 max-w-full">
          <AlignmentTypeahead
            autoFocus
            value={selectedId}
            onChange={handlePick}
            options={options}
            disabled={disabled}
            placeholder={t('alignment.typeToSearch')}
          />
        </div>
      )}
    </div>
  );
};
